import { Menu, Transition } from '@headlessui/react'
import { format } from 'date-fns'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { IoNotificationsOutline } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'

import {
  useGetUnreadHelpdeskNotificationsQuery,
  useMarkHelpdeskNotificationReadMutation,
} from '../../api/services/helpdeskNotificationsApi'

const HelpdeskNotificationsDropdown = ({ light = false }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: helpdeskNotifications, isLoading } = useGetUnreadHelpdeskNotificationsQuery()
  const [markHelpdeskAsRead] = useMarkHelpdeskNotificationReadMutation()

  const helpdeskUnread = helpdeskNotifications?.length ?? 0

  const handleHelpdeskClick = (notification) => {
    if (!notification.is_read) {
      markHelpdeskAsRead(notification.notification_id)
    }
    navigate(`/helpdesk/${notification.ticket_id}`)
  }

  const handleViewAll = () => {
    navigate('/helpdesk')
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`relative flex h-9 w-9 items-center justify-center rounded-full duration-300 hover:cursor-pointer ${
          light ? 'text-white hover:bg-white/20' : 'text-[#073C59] hover:bg-sky-100'
        }`}
        aria-label={t('support.notifications.dropdown_title')}
      >
        <IoNotificationsOutline size={22} />
        {helpdeskUnread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white border-2 border-white">
            {helpdeskUnread > 9 ? '9+' : helpdeskUnread}
          </span>
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 w-96 mt-2 origin-top-right bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none max-h-[600px] overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 bg-[#006EAB]">
            <h3 className="text-lg font-semibold text-white">{t('support.notifications.dropdown_title')}</h3>
            <p className="text-sm text-gray-200 mt-1">
              {t('support.notifications.dropdown_count', { count: helpdeskUnread })}
            </p>
          </div>
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">{t('support.notifications.loading')}</div>
            ) : helpdeskUnread === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <IoNotificationsOutline className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm">{t('support.notifications.no_notifications')}</p>
              </div>
            ) : (
              <div className="py-2">
                {(helpdeskNotifications ?? []).map((notification) => (
                  <Menu.Item key={notification.notification_id}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => handleHelpdeskClick(notification)}
                        className={`${
                          active ? 'bg-gray-50' : 'bg-blue-50'
                        } w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded bg-gray-100 text-gray-600">
                                {t(`support.notifications.event_type.${notification.event_type}`)}
                              </span>
                              <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                            </div>
                            <h4 className="text-sm font-semibold text-gray-900 truncate mb-1">{notification.title}</h4>
                            <p className="text-xs text-gray-500 line-clamp-2">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {format(new Date(notification.created_at), 'dd.MM.yyyy HH:mm')}
                            </p>
                          </div>
                        </div>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-gray-200 px-4 py-2">
            <button
              type="button"
              onClick={handleViewAll}
              className="w-full text-center text-sm font-medium text-[#006EAB] hover:underline py-2"
            >
              {t('support.notifications.view_all_tickets')}
            </button>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default HelpdeskNotificationsDropdown
